<template lang="html">
  <div class="charging-layout">
    <page-header />

    <main class="charging__main">
      <div class="charging__card charging__card--location">
        <div>
          <p>{{ selectedCharger.connector?.evseId }}</p>
          <p>{{ selectedCharger.address }}</p>
        </div>
      </div>

      <div class="charging__card charging__card--type">
        <span class="charging-card__letter">
          {{ selectedCharger.connector?.label }}
        </span>
        <div>
          <p class="charging-card__type">
            {{ connectorTypeName }}
          </p>
          <div class="charging-card__info charger__info">
            <img
              v-if="selectedCharger.connector?.mode === 'DC'"
              class="charger__info-icon"
              src="@/assets/images/charger-dc-icon.svg"
              alt=""
            />
            <img
              v-else
              class="charger__info-icon"
              src="@/assets/images/charger-ac-icon.svg"
              alt=""
            />
            <span class="charger__info-text"
              >{{ price }} | {{ selectedCharger.connector?.voltage }} V |
              {{ selectedCharger.connector?.current }} A</span
            >
          </div>
          <div class="charging-card__power charger__power">
            <img
              class="charger__power-icon"
              src="@/assets/images/charger-power.svg"
              alt=""
            />
            <span class="charger__power-text"
              >{{
                selectedCharger.connector?.power
                  ? selectedCharger.connector.power / 1000
                  : "N/a"
              }}
              kW</span
            >
          </div>
          <p class="charging-card__price">
            {{ price }}
            <img
              class="charging-card__price-icon"
              src="@/assets/images/info-circle-pricing.svg"
              alt=""
            />
          </p>
        </div>
        <img
          :src="`${config.imagesPath}/charger-${connectorTypeSlug}.svg`"
          alt=""
          class="charging-card__connector-type-icon"
        />
      </div>

      <div class="charging-time">
        <div class="charging__card charging__card--time">
          <p>{{ $t("chargingPage.chargingLayout.chargingStarted") }}</p>
          <p>
            {{ displayTime(chargingStartedAt) }}
          </p>
        </div>
        <div v-if="endTime" class="charging__card charging__card--time">
          <p>{{ $t("chargingPage.chargingLayout.chargingFinished") }}</p>
          <p>{{ displayTime(endTime) }}</p>
        </div>
      </div>

      <div class="charging__card charging__card--progress">
        <p class="charging__card--text">
          {{ $t("chargingPage.chargingLayout.subTitle") }}
        </p>
      </div>

      <div class="charging-info">
        <img
          src="@/assets/images/info-circle-charging.svg"
          alt=""
          class="charging-info__icon"
        />
        <p>{{ $t(chargingInfo) }}</p>
      </div>
    </main>

    <div v-if="stopButtonVisible" class="charging-stop">
      <button-component
        block
        :loading="submitIsLoading"
        primary
        @click="stopClick()"
      >
        {{ $t("chargingPage.chargingLayout.stopButton") }}
      </button-component>
    </div>

    <confirm-modal
      v-if="isConfirmModalActive"
      yes-button-color-class="btn-primary"
      :main-title="$t('chargingPage.confirm.stopCharging.mainTitle')"
      :sub-title="$t('chargingPage.confirm.stopCharging.subTitle')"
      :yes-title="$t('chargingPage.confirm.stopCharging.yesTitle')"
      :no-title="$t('chargingPage.confirm.stopCharging.noTitle')"
      @confirm="confirmStopCharge"
      @cancel="handleConfirmModalCancel"
    />
    <feedback-modal
      v-if="isFeedbackModalActive"
      :main-title="$t(feedbackStopMainTitle)"
      :sub-title="$t(feedbackStopSubTitle)"
      :success="feedbackStopSuccess"
      @confirm="handleFeedbackModalOkClick"
    />
  </div>
</template>

<script lang="ts" setup>
import { GuestChargingService } from "@/services/guest-charging-service"
import { PriceService } from "@/services/price-service"
import ConfirmModal from "@/components/ui/ConfirmModal"
import FeedbackModal from "@/components/ui/FeedbackModal"
import config from "../../config/index"
import PageHeader from "../ui/PageHeader.vue"
import ButtonComponent from "../ui/ButtonComponent.vue"
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useDictionaryStore } from "../../store/dictionary"
import { storeToRefs } from "pinia"
import { chargerTypes, chargerTypesFromBackend } from "@/models/common"
import type { Charger } from "@/models/common"
import { ChargingStatus } from "@/models/enums"

interface ChargingLayoutProps {
  selectedCharger?: Charger
  chargingStartedAt?: Date
}

const props = withDefaults(defineProps<ChargingLayoutProps>(), {
  selectedCharger: () => ({ address: "" }),
  chargingStartedAt: () => new Date()
})

const dictionaryStore = useDictionaryStore()
const { dynamicConfig } = storeToRefs(dictionaryStore)
const i18n = useI18n()
const router = useRouter()

const feedbackStopMainTitle = ref("")
const feedbackStopSubTitle = ref("")
const feedbackStopSuccess = ref(false)
const stopButtonVisible = ref(false)
const pollingIntervalId = ref<ReturnType<typeof setInterval> | null>(null)
const chargingInfo = ref(
  "chargingPage.chargingLayout.chargingInfo.startCommandAccepted"
)
const isConfirmModalActive = ref(false)
const isFeedbackModalActive = ref(false)
const submitIsLoading = ref(false)
const endTime = ref<Date | null>(null)

const price = computed(() => {
  return PriceService.getPriceLabel(
    props.selectedCharger.connector?.price,
    i18n
  )
})

const connectorTypeSlug = computed(() => {
  const connectorType = props.selectedCharger.connector?.connectorType

  return connectorType ? chargerTypesFromBackend[connectorType] : "type2"
})

const connectorTypeName = computed(() => {
  return chargerTypes[connectorTypeSlug.value]
    ? chargerTypes[connectorTypeSlug.value]
    : chargerTypes.type2
})

const stopClick = () => {
  isConfirmModalActive.value = true
}

const feedbackStopOk = () => {
  feedbackStopSuccess.value = true
  feedbackStopMainTitle.value = "chargingPage.chargingLayout.stopOk.mainTitle"
  feedbackStopSubTitle.value = "chargingPage.chargingLayout.stopOk.subTitle"
  isFeedbackModalActive.value = true
}

const afterSuccesfulStop = () => {
  const url = dynamicConfig?.value?.redirectUrl
  if (url) {
    window.location.href = url
  } else {
    router.push({ name: "LandingPage" })
  }
}

const feedbackStopError = (error: any) => {
  console.error("ChargingLayout feedbackStopError")

  feedbackStopSuccess.value = false

  if (error.messageCode === "guestCharging.session.alreadyStopped") {
    feedbackStopMainTitle.value =
      "errors.chargingErrors.alreadyStoppedError.mainTitle"
    feedbackStopSubTitle.value =
      "errors.chargingErrors.alreadyStoppedError.subTitle"
  } else {
    feedbackStopMainTitle.value = "errors.defaultError.mainTitle"
    feedbackStopSubTitle.value = "errors.defaultError.subTitle"
  }

  isFeedbackModalActive.value = true
}

const confirmStopCharge = async () => {
  endTime.value = new Date()
  submitIsLoading.value = true
  isConfirmModalActive.value = false

  try {
    const evseId = props.selectedCharger.connector?.evseId

    if (evseId) {
      await GuestChargingService.stopCharging(evseId)
    }
    feedbackStopOk()
    setTimeout(() => {
      afterSuccesfulStop()
    }, 5000)
  } catch (e) {
    console.error(e)
    feedbackStopError(e)
  } finally {
    submitIsLoading.value = false
  }
}

const getStatus = async () => {
  try {
    const { data } = await GuestChargingService.getChargingStatus()
    const { chargingStatus } = data

    const actions: Partial<Record<ChargingStatus, () => void>> = {
      [ChargingStatus.STOPPED]: () => afterSuccesfulStop(),
      [ChargingStatus.STARTED]: () => {
        stopButtonVisible.value = true
        chargingInfo.value = "chargingPage.chargingLayout.chargingInfo.started"
      },
      [ChargingStatus.INVALID]: () => {
        feedbackStopMainTitle.value =
          "errors.chargingStatusErrors.invalid.mainTitle"
        feedbackStopSubTitle.value =
          "errors.chargingStatusErrors.invalid.subTitle"
        isFeedbackModalActive.value = true
      },
      [ChargingStatus.PENDING]: () => {
        chargingInfo.value = "chargingPage.chargingLayout.chargingInfo.pending"
      },
      [ChargingStatus.START_COMMAND_ACCEPTED]: () => {
        chargingInfo.value =
          "chargingPage.chargingLayout.chargingInfo.startCommandAccepted"
      }
    }

    actions[chargingStatus as ChargingStatus]?.()
  } catch (error) {
    console.error(error)
  }
}

const pollStatus = () => {
  pollingIntervalId.value = setInterval(() => {
    getStatus()
  }, 10000)
}

const handleFeedbackModalOkClick = () => {
  if (!feedbackStopSuccess.value) {
    afterSuccesfulStop()
  }
  isFeedbackModalActive.value = false
}

const handleConfirmModalCancel = () => {
  isConfirmModalActive.value = false
}

const displayTime = (date: Date) => {
  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`
}

onMounted(() => {
  getStatus()
  pollStatus()
})

onBeforeUnmount(() => {
  if (pollingIntervalId.value) {
    clearInterval(pollingIntervalId.value)
  }
})
</script>
